// src/components/BreakdownModal.js
import React from 'react';
import {
  Box,
  Typography,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Avatar,
  Modal,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import TimerIcon from '@mui/icons-material/Timer';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

const BreakdownModal = ({
  open,
  onClose,
  costBreakdown,
  dependentsData,
  includeAttestation,
  onAttestationToggle,
  offerCountdown,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Modal 
      open={open} 
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: isMobile ? 2 : 4,
      }}
    >
      <Paper
        elevation={24}
        sx={{
          maxWidth: '95%',
          width: isMobile ? '100%' : '600px',
          borderRadius: isMobile ? '12px' : '16px',
          background: 'linear-gradient(to bottom, #ffffff, #f8f9fa)',
          position: 'relative',
          overflow: 'hidden',
          maxHeight: '90vh',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
            '&:hover': {
              background: '#555',
            },
          },
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #2196F3 30%, #21CBF3 90%)',
            p: isMobile ? 2 : 3,
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
              mb: 2,
            }}
          >
            Your Family Visa Journey Plan 🌟
          </Typography>
          
          {dependentsData.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {dependentsData.map((member, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '20px',
                    p: 1,
                    gap: 1,
                  }}
                >
                  <Avatar
                    alt={member.relation}
                    src={member.relation.toLowerCase() === 'spouse' ? '/images/spouse.png' : '/images/child.png'}
                    sx={{ 
                      width: isMobile ? 32 : 40, 
                      height: isMobile ? 32 : 40,
                      border: '2px solid white',
                    }}
                  />
                  <Typography sx={{ color: 'white', fontSize: isMobile ? '0.8rem' : '1rem' }}>
                    {member.relation}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Main Content */}
        <Box sx={{ p: isMobile ? 2 : 3 }}>
          {/* Attestation Toggle */}
          <Paper
            elevation={3}
            sx={{
              p: 2,
              mb: 3,
              background: 'linear-gradient(to right, #f5f5f5, #ffffff)',
              borderRadius: '12px',
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}>
              <Typography sx={{ 
                fontSize: isMobile ? '0.9rem' : '1rem',
                flex: 1,
              }}>
                Include Document Attestation
              </Typography>
              <Switch
                checked={includeAttestation}
                onChange={onAttestationToggle}
                color="primary"
              />
            </Box>
          </Paper>

          {/* Cost Breakdown */}
          {costBreakdown && Array.isArray(costBreakdown) && (
            <Box sx={{ animation: 'fadeIn 0.5s ease-out' }}>
              {costBreakdown.map((memberCost, index) => (
                <Paper
                  key={index}
                  elevation={2}
                  sx={{
                    mb: 3,
                    borderRadius: '12px',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{
                    background: 'linear-gradient(to right, #f5f5f5, #ffffff)',
                    p: 2,
                    borderBottom: '1px solid #eee',
                  }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {dependentsData[index]?.relation} ({dependentsData[index]?.location})
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size={isMobile ? "small" : "medium"}>
                      <TableBody>
                        {Object.entries(memberCost)
                          .filter(([key, value]) => 
                            key !== 'total' && value !== 0 && 
                            ['file_opening', 'entry_permit', 'status_change', 'medical', 'visa_stamping', 'emirates_id', 'attestation']
                            .includes(key)
                          )
                          .map(([key, value]) => (
                            <TableRow key={key}>
                              <TableCell sx={{ fontSize: isMobile ? '0.8rem' : '1rem' }}>
                                {key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: isMobile ? '0.8rem' : '1rem' }}>
                                AED {value.toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))
                        }
                        <TableRow>
                          <TableCell sx={{ 
                            fontWeight: 'bold',
                            fontSize: isMobile ? '0.9rem' : '1.1rem',
                          }}>
                            Total
                          </TableCell>
                          <TableCell align="right" sx={{ 
                            fontWeight: 'bold',
                            fontSize: isMobile ? '0.9rem' : '1.1rem',
                            color: theme.palette.primary.main,
                          }}>
                            AED {memberCost.total.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              ))}

              {/* Grand Total */}
              <Paper
                elevation={4}
                sx={{
                  p: 2,
                  mb: 3,
                  background: 'linear-gradient(135deg, #2196F3 30%, #21CBF3 90%)',
                  borderRadius: '12px',
                }}
              >
                <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  sx={{
                    textAlign: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                >
                  Grand Total: AED {costBreakdown.reduce((acc, member) => acc + member.total, 0).toFixed(2)}
                </Typography>
              </Paper>
            </Box>
          )}

          {/* Special Offer Section */}
          <Paper
            elevation={3}
            sx={{
              p: 2,
              mb: 3,
              background: 'linear-gradient(to right, #fff3e0, #fff)',
              borderRadius: '12px',
              border: '1px solid #ffe0b2',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                mb: 2,
              }}>
                <TimerIcon color="warning" />
                <Typography 
                  variant="body1" 
                  color="warning.dark"
                  sx={{ fontWeight: 500 }}
                >
                  Special Offer Expires in {offerCountdown} second{offerCountdown !== 1 ? 's' : ''}!
                </Typography>
              </Box>
              <Button
                 href="https://wa.me/971525200725" 
                variant="contained"
                startIcon={<LocalOfferIcon />}
                sx={{
                  background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)',
                  mb: 2,
                  '&:hover': {
                    background: 'linear-gradient(45deg, #F57C00 30%, #FFA726 90%)',
                  },
                }}
              >
                Grab Your Offer Now
              </Button>
            </Box>
          </Paper>

          {/* Action Buttons */}
          <Box sx={{ 
            display: 'flex',
            gap: 2,
            flexDirection: isMobile ? 'column' : 'row',
          }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<WhatsAppIcon />}
              sx={{
                background: '#25D366',
                '&:hover': {
                  background: '#128C7E',
                },
              }}
              href="https://wa.me/971525200725" // Replace with your WhatsApp number
              target="_blank"
            >
              Chat on WhatsApp
            </Button>
            <Button
              onClick={onClose}
              variant="outlined"
              fullWidth
            >
              Close
            </Button>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default BreakdownModal;
